import { nodesFlux } from '@candulabs/craft';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { createSelector } from 'reselect';

import { ContentPlacement, TooltipPlacement } from '@candu-ai/placement/types';
import { CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor } from 'craft/hooks/useEditor';
import { useEditorSelector } from 'craft/hooks/useEditorSelector';
import { CustomNodeData, DocumentPlacements } from 'craft/types';

interface UseTooltipPlacementProps {
  placement: ContentPlacement;
  setPlacement: (placement?: TooltipPlacement) => void;
  addPlacement: (placement: TooltipPlacement) => void;
}

const EMPTY_PLACEMENTS = {} as DocumentPlacements;
Object.freeze(EMPTY_PLACEMENTS);

const createPlacementSelector = (nodeId?: string) =>
  createSelector(
    nodesFlux.selectors.getNodeData(nodeId),
    nodesFlux.selectors.getNodeCustom(CRAFT_ROOT_ID),
    (nodeData: any, rootCustom: CustomNodeData) => {
      const placementId = nodeData?.props?.placementId;
      return {
        placementId,
        placement: (rootCustom?.placements ?? EMPTY_PLACEMENTS)[placementId],
      };
    },
  );

export const useTooltipPlacement = (nodeId?: string): UseTooltipPlacementProps => {
  const { actions } = useEditor();
  const selector = useMemo(() => createPlacementSelector(nodeId), [nodeId]);
  const { placementId, placement } = useEditorSelector(selector);

  const setPlacement = (newPlacement?: TooltipPlacement) => {
    actions.setCustom(CRAFT_ROOT_ID, (custom: CustomNodeData) => {
      if (!custom.placements) {
        // eslint-disable-next-line no-param-reassign
        custom.placements = {};
      }
      if (!newPlacement) {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        delete custom.placements[placementId];
      }
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      custom.placements[placementId] = newPlacement;
    });
  };

  const addPlacement = (newPlacement: TooltipPlacement) => {
    const newPlacementId = nanoid(6);
    actions.produceState((newState) => {
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      newState.nodes[nodeId].data.props.placementId = newPlacementId;
      if (!newState.nodes[CRAFT_ROOT_ID].data.custom.placements) {
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        newState.nodes[CRAFT_ROOT_ID].data.custom.placements = {};
      }
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      newState.nodes[CRAFT_ROOT_ID].data.custom.placements[newPlacementId] = newPlacement;
    });
  };

  return { placement, setPlacement, addPlacement };
};
